import type { Chain, ChainDefinitionCurrency } from "@opensea/app-kit"
import { useChains } from "@opensea/app-kit"
import { graphql, readFragment, useQuery } from "@opensea/graphql"
import { bn } from "@opensea/utils"
import { useMemo } from "react"
import { getChainArchFromAddress } from "../lib/address"
import { useAddress } from "../polychain/hooks"
import type { CurrencyIdentifier } from "../providers"
import type { Balance } from "../providers/BalancesProvider"
import { ContextBalanceFragment } from "../providers/BalancesProvider/ContextBalance.fragment"

/**
 * Filter balances that are sufficient for a given USD price
 */
export function filterSufficientBalances(
  balances: Balance[],
  totalUsdPrice: number,
): Balance[] {
  return balances.filter(balance => bn(balance.usdValue).gte(totalUsdPrice))
}

/**
 * Options for useSufficientBalances hook
 */
type UseSufficientBalancesOptions = {
  /** Total USD price to check sufficiency against */
  totalUsdPrice: number

  /** Only show this specific currency if provided */
  onlyCurrency?: CurrencyIdentifier

  /** Preferred currency to put first in the list */
  preferredCurrency?: CurrencyIdentifier

  /** Target NFT chain for smart sorting */
  targetChain?: string
}

/**
 * Internal function to get sufficient balances with smart sorting
 */
function findSufficientBalances(
  totalUsdPrice: number,
  balances: Balance[],
  onlyCurrency?: CurrencyIdentifier,
  preferredCurrency?: CurrencyIdentifier,
  targetChain?: string,
  getNativeCurrency?: (chain: string) => ChainDefinitionCurrency,
  getWrappedNativeCurrency?: (
    chain: string,
  ) => ChainDefinitionCurrency | undefined,
): Balance[] {
  const balancesForChain = balances.filter(c =>
    onlyCurrency
      ? c.currency.chainIdentifier === onlyCurrency.chainIdentifier &&
        c.currency.contractAddress === onlyCurrency.contractAddress
      : true,
  )
  const sufficientBalances = filterSufficientBalances(
    balancesForChain,
    totalUsdPrice,
  )

  // Smart sorting priority:
  // 1. Native currency on same chain (ETH on ethereum)
  // 2. Wrapped native on same chain (WETH on ethereum)
  // 3. Native/Wrapped native on other chains (ETH on base, WETH on arbitrum)
  // 4. Random token on same chain (USDC on ethereum)
  // 5. Random token on different chain (USDC on polygon)
  sufficientBalances.sort((a, b) => {
    const getPriority = (balance: Balance): number => {
      const isOnTargetChain =
        targetChain && balance.currency.chainIdentifier === targetChain
      const nativeCurrency =
        targetChain && getNativeCurrency
          ? getNativeCurrency(balance.currency.chainIdentifier)
          : undefined
      const wrappedNativeCurrency =
        targetChain && getWrappedNativeCurrency
          ? getWrappedNativeCurrency(balance.currency.chainIdentifier)
          : undefined

      const isNative =
        nativeCurrency &&
        balance.currency.contractAddress === nativeCurrency.contractAddress
      const isWrappedNative =
        wrappedNativeCurrency &&
        balance.currency.contractAddress ===
          wrappedNativeCurrency.contractAddress

      if (isNative && isOnTargetChain) {
        return 1 // Highest priority
      }
      if (isWrappedNative && isOnTargetChain) {
        return 2
      }
      if (isNative || isWrappedNative) {
        return 3 // Native/wrapped on other chains
      }
      if (isOnTargetChain) {
        return 4 // Random token on same chain
      }
      return 5 // Random token on different chain
    }

    const aPriority = getPriority(a)
    const bPriority = getPriority(b)

    // Sort by priority first (lower number = higher priority)
    if (aPriority !== bPriority) {
      return aPriority - bPriority
    }

    // If same priority, sort by USD value (higher value first)
    const aValue = bn(a.usdValue)
    const bValue = bn(b.usdValue)
    return bValue.gt(aValue) ? 1 : bValue.lt(aValue) ? -1 : 0
  })

  // If there's a preferred currency, move it to the front of the list
  if (preferredCurrency) {
    const preferredIndex = sufficientBalances.findIndex(
      balance =>
        balance.currency.chainIdentifier ===
          preferredCurrency.chainIdentifier &&
        balance.currency.contractAddress === preferredCurrency.contractAddress,
    )

    if (preferredIndex > 0) {
      const [preferredBalance] = sufficientBalances.splice(preferredIndex, 1)
      sufficientBalances.unshift(preferredBalance)
    }
  }

  return sufficientBalances
}

/**
 * Checks if the address is compatible with the given chain.
 * They must have the same architecture (EVM or SVM).
 */
export function isAddressCompatibleWithChain(
  address: string | undefined,
  chain: Chain | undefined,
): boolean {
  if (!(address && chain)) {
    return false
  }

  const addressArch = getChainArchFromAddress(address)
  return addressArch === chain.arch
}

const useSufficientBalancesQuery = graphql(
  `
    query UseSufficientBalancesQuery($address: Address!) {
      currencyOptions(address: $address) {
        ...ContextBalance
      }
    }
  `,
  [ContextBalanceFragment],
)

const CONTEXT = {
  suspense: false,
  fetchPolicy: "network-only",
}

/**
 * React hook to get sufficient balances that can be used for purchases
 */
export function useSufficientBalances({
  totalUsdPrice,
  onlyCurrency,
  preferredCurrency,
  targetChain,
}: UseSufficientBalancesOptions) {
  const address = useAddress()
  const { getNativeCurrency, getWrappedNativeCurrency } = useChains()

  const [{ data, fetching }] = useQuery({
    query: useSufficientBalancesQuery,
    variables: {
      address: address ?? "",
    },
    pause: !address,
    context: CONTEXT,
  })

  // TODO: Known violation of fragments but doing this for now as a quick fix
  const currencyOptions =
    data?.currencyOptions.map(balance =>
      readFragment(ContextBalanceFragment, balance),
    ) ?? []

  return useMemo(
    () => ({
      balances: findSufficientBalances(
        totalUsdPrice,
        currencyOptions,
        onlyCurrency,
        preferredCurrency,
        targetChain,
        getNativeCurrency,
        getWrappedNativeCurrency,
      ),
      loading: fetching,
    }),
    [
      currencyOptions,
      fetching,
      totalUsdPrice,
      onlyCurrency,
      preferredCurrency,
      targetChain,
      getNativeCurrency,
      getWrappedNativeCurrency,
    ],
  )
}
